import { useState } from 'react'
import { formatApiDate } from '~/shared/lib/date'
import type { FC } from 'react'
import { Box, Typography } from '@mui/material'

import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded'
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'

import {
  programsAddPatient,
  ProgramsAddPatientCreateBodyParams,
  programsRemovePatient,
  ProgramsRemovePatientCreateBodyParams,
  ProgramsPeopleEnrollmentListResponse,
} from '@fireflyhealth/api'
import { usePrograms, useListProgramEnrollmentByProgram } from './ProgramTabService'
import { usePatientDetail } from '~/features/member-profile'
import { patientsSlice } from '~/entities/person'
import { queueNotification } from '~/shared/notifications/actions'
import {
  BENEFIT_PROGRAM_STATUSES,
  BENEFIT_PROGRAM_UID,
  DISCHARGE_IN_PROGRESS_TEXT,
  getPrimaryCareStatusDisplay,
  NO_ENROLLMENT_TEXT,
  PRE_ESTABLISHMENT_STATUSES,
  PRIMARY_CARE_PROGRAM_UID,
  PRIMARY_CARE_STATUS,
  READABLE_PROGRAM_NAMES,
} from '~/entities/program'
import type { PatientUser } from '~/shared/types'
import { SectionCard, StatusPill, type Tone } from '~/shared/ui'
import { Button, MenuItem, Select, Switch, TextField } from '@mui/material'
import { titleCase } from '~/shared/lib/displayText'
import {
  PROGRAM_UNENROLL_CLIENTS,
  REASON_OPTIONS,
  requiresUnenrollReason,
} from './CaptureProgramUnenrollReason'
import PrimaryCareProgramMenu from './PrimarycareProgramMenu/PrimaryCareProgramMenu'
import { useAppDispatch } from '~/app/store/hooks'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

const readableProgramName = (programUid: string) =>
  READABLE_PROGRAM_NAMES[programUid as keyof typeof READABLE_PROGRAM_NAMES] || programUid

type ProgramWithDisplayName = ReturnType<typeof usePrograms>['result'][number] & {
  display_name: string
}

const STATUS_TONE: Record<string, Tone> = {
  Established: 'success',
  Enrolled: 'success',
  Elected: 'info',
  'Not Established': 'neutral',
  Churned: 'error',
  Terminated: 'error',
}

interface EnrollmentStep {
  status: string
  eventDate: string | null
  reason?: string
}

// Same view-model the legacy ProgramStatusStepper builds in an effect, as a
// pure function: readable status per enrollment (by program), the parsed
// churn/discharge reason, and the not-yet-reached statuses appended after the
// current one.
const buildEnrollmentSteps = (
  program: ProgramWithDisplayName,
  enrollments: ProgramsPeopleEnrollmentListResponse,
): { steps: EnrollmentStep[]; activeStep: number; churnedReason?: string } => {
  const steps: EnrollmentStep[] = []
  let churnedReason: string | undefined = undefined

  if (!enrollments || enrollments.length === 0) {
    return { steps, activeStep: -1, churnedReason }
  }

  for (const enrollment of enrollments) {
    let reason: string | undefined = undefined
    if (enrollment.reason) {
      reason =
        enrollment.reason.includes(DISCHARGE_IN_PROGRESS_TEXT) &&
        enrollment.reason.split(':').length === 2
          ? enrollment.reason.split(':')[1]
          : enrollment.reason
    }
    churnedReason = reason

    let readableStatus = ''
    if (program.uid == PRIMARY_CARE_PROGRAM_UID) {
      // Kept as the raw backend value — tone lookups and the program menu key on it; the
      // display label is applied at the render sites via getPrimaryCareStatusDisplay.
      readableStatus = enrollment.status || PRIMARY_CARE_STATUS.UNKNOWN
    } else if (program.uid == BENEFIT_PROGRAM_UID) {
      readableStatus =
        BENEFIT_PROGRAM_STATUSES[
          (enrollment.status ?? 'Unknown') as keyof typeof BENEFIT_PROGRAM_STATUSES
        ] ??
        (enrollment.status || 'Unknown')
    }
    steps.push({
      status: readableStatus,
      eventDate: formatApiDate(enrollment.period.lower, 'MM/dd/yyyy, hh:mm a') ?? '',
      reason,
    })
  }

  const activeStep = enrollments.length - 1
  const lastStatus = enrollments[enrollments.length - 1]?.status

  if (program.uid == PRIMARY_CARE_PROGRAM_UID) {
    if (lastStatus && PRE_ESTABLISHMENT_STATUSES.some(status => status === lastStatus)) {
      // Care-team selection moves a pre-establishment member to Not Established, so members on
      // Episodic Care / Unknown see that step before the shared Established → Churned path.
      if (lastStatus !== PRIMARY_CARE_STATUS.NOT_ESTABLISHED) {
        steps.push({ status: PRIMARY_CARE_STATUS.NOT_ESTABLISHED, eventDate: null, reason: '' })
      }
      steps.push({ status: PRIMARY_CARE_STATUS.ESTABLISHED, eventDate: null, reason: '' })
      steps.push({ status: PRIMARY_CARE_STATUS.CHURNED, eventDate: null, reason: '' })
    }
    if (lastStatus == PRIMARY_CARE_STATUS.ESTABLISHED) {
      steps.push({ status: PRIMARY_CARE_STATUS.CHURNED, eventDate: null, reason: '' })
    }
  }
  if (program.uid == BENEFIT_PROGRAM_UID) {
    if (lastStatus == BENEFIT_PROGRAM_STATUSES.Elected) {
      steps.push({ status: BENEFIT_PROGRAM_STATUSES.Enrolled, eventDate: null, reason: '' })
      steps.push({ status: BENEFIT_PROGRAM_STATUSES.Terminated, eventDate: null, reason: '' })
    }
    if (lastStatus == BENEFIT_PROGRAM_STATUSES.Enrolled) {
      steps.push({ status: BENEFIT_PROGRAM_STATUSES.Terminated, eventDate: null, reason: '' })
    }
  }

  return { steps, activeStep, churnedReason }
}

const PROGRAM_ICONS: Record<string, { Icon: typeof HealthAndSafetyRoundedIcon; tone: Tone }> = {
  [BENEFIT_PROGRAM_UID]: { Icon: HealthAndSafetyRoundedIcon, tone: 'warning' },
  [PRIMARY_CARE_PROGRAM_UID]: { Icon: MedicalServicesRoundedIcon, tone: 'info' },
}

const SectionHeading: FC<{ title: string; caption: string }> = ({ title, caption }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
    <Typography
      sx={{
        fontSize: '1.2rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'text.secondary',
      }}
    >
      {title}
    </Typography>
    <Typography sx={{ fontSize: '1.15rem', color: 'text.secondary' }}>{caption}</Typography>
  </Box>
)

// Status display labels are cosmetic; tone lookups and the program menu key on the raw status.
const BaselineProgramCard: FC<{
  program: ProgramWithDisplayName
  enrollments: ProgramsPeopleEnrollmentListResponse
  patient: PatientUser
}> = ({ program, enrollments, patient }) => {
  const { steps, activeStep, churnedReason } = buildEnrollmentSteps(program, enrollments)
  const currentStatus = activeStep >= 0 ? steps[activeStep]?.status : undefined
  const enrollmentStatus =
    activeStep >= 0 ? (enrollments[activeStep]?.status ?? undefined) : undefined
  const { Icon, tone } = PROGRAM_ICONS[program.uid] ?? {
    Icon: MedicalServicesRoundedIcon,
    tone: 'neutral',
  }

  return (
    <SectionCard sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          aria-hidden
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 42,
            height: 42,
            borderRadius: 1,
            flex: '0 0 auto',
            bgcolor: theme.palette.tones[tone].bg,
            color: theme.palette.tones[tone].fg,
          })}
        >
          <Icon sx={{ fontSize: '2.2rem' }} />
        </Box>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary', mr: 1 }}>
          {program.display_name}
        </Typography>
        {currentStatus && (
          <StatusPill
            tone={STATUS_TONE[currentStatus] ?? 'neutral'}
            label={getPrimaryCareStatusDisplay(currentStatus)}
          />
        )}
        <Box sx={{ ml: 'auto', flex: '0 0 auto' }}>
          {program.uid === PRIMARY_CARE_PROGRAM_UID && (
            <PrimaryCareProgramMenu
              enrollmentStatus={enrollmentStatus}
              churnedReason={churnedReason}
              patient={patient}
              // The redux patient is camelized at runtime (apiClient.rest),
              // so this reads the same value the legacy stepper passes even
              // though PatientUser types the field as onboarding_status.
              onboardingStatus={
                (patient as unknown as { onboardingStatus?: string })?.onboardingStatus
              }
            />
          )}
        </Box>
      </Box>
      <Box sx={{ mt: 2 }}>
        {steps.length === 0 && (
          <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
            {NO_ENROLLMENT_TEXT[program.uid as keyof typeof NO_ENROLLMENT_TEXT]}
          </Typography>
        )}
        {steps.map((step, index) => {
          const isDone = index < activeStep
          const isCurrent = index === activeStep
          return (
            <Box key={`${step.status}_${index}`} sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: '0 0 auto',
                }}
              >
                {isDone || isCurrent ? (
                  <CheckCircleRoundedIcon
                    sx={theme => ({
                      fontSize: '2rem',
                      // Done steps are neutral (not green): green read as a
                      // status semantic rather than "past step". Current step
                      // stays blue, matching the legacy stepper.
                      color: isCurrent ? theme.palette.primary.main : theme.palette.grey[400],
                    })}
                  />
                ) : (
                  <RadioButtonUncheckedRoundedIcon sx={{ fontSize: '2rem', color: 'grey.200' }} />
                )}
                {index < steps.length - 1 && (
                  <Box
                    sx={theme => ({
                      width: '2px',
                      flex: 1,
                      minHeight: 14,
                      bgcolor:
                        isDone || isCurrent ? theme.palette.primary.light : theme.palette.divider,
                    })}
                  />
                )}
              </Box>
              <Box sx={{ pb: index < steps.length - 1 ? 1.75 : 0, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
                  <Typography
                    sx={{
                      fontSize: '1.35rem',
                      fontWeight: 600,
                      color: isCurrent ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {getPrimaryCareStatusDisplay(step.status)}
                  </Typography>
                  {step.eventDate && (
                    <Typography
                      sx={theme => ({
                        fontFamily: theme.typography.fontFamilyMono,
                        fontSize: '1.15rem',
                        color: 'text.secondary',
                      })}
                    >
                      {step.eventDate}
                    </Typography>
                  )}
                </Box>
                {step.reason && (
                  <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
                    {step.reason}
                  </Typography>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </SectionCard>
  )
}

// Design-refresh unenroll-reason panel: same typed per-program client and
// payload as CaptureProgramUnEnrollReason, in the card language — plus a
// Cancel path and a submit guard the legacy panel lacks (it could only be
// closed by submitting, even with an empty reason).
const UnenrollReasonPanelRefresh: FC<{
  patientId: number
  programUid: keyof typeof PROGRAM_UNENROLL_CLIENTS
  onToggle: (enrolled: boolean, programUid: string) => void
  onClose: () => void
}> = ({ patientId, programUid, onToggle, onClose }) => {
  const [unenrollReason, setUnenrollReason] = useState<string>('')
  const [otherReasonNote, setOtherReasonNote] = useState<string>('')

  const captureReason = async () => {
    await PROGRAM_UNENROLL_CLIENTS[programUid].partialUpdate(
      { user_id: `${patientId}` },
      {
        data: {
          unenroll_reason:
            otherReasonNote == ''
              ? unenrollReason
              : unenrollReason.concat(' - ').concat(otherReasonNote),
        },
      },
    )
    onToggle(false, programUid)
    onClose()
  }

  return (
    <Box
      sx={theme => ({
        mt: 1.5,
        p: 1.5,
        borderRadius: `${theme.shape.borderRadius}px`,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${theme.palette.tones.warning.fg}`,
      })}
    >
      <Typography sx={{ fontSize: '1.3rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>
        Unenroll patient
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Select
          value={unenrollReason}
          onChange={e => setUnenrollReason(e.target.value)}
          size="small"
          displayEmpty
          sx={{ minWidth: 200, fontSize: '1.3rem' }}
          inputProps={{ 'aria-label': 'Select a reason' }}
        >
          <MenuItem value="">
            <em>Select a reason</em>
          </MenuItem>
          {Object.entries(REASON_OPTIONS).map(([reasonKey, reasonValue]) => (
            <MenuItem key={reasonKey} value={reasonValue} sx={{ fontSize: '1.3rem' }}>
              {titleCase(reasonValue)}
            </MenuItem>
          ))}
        </Select>
        {unenrollReason === REASON_OPTIONS.OTHER && (
          <TextField
            value={otherReasonNote}
            onChange={e => setOtherReasonNote(e.target.value)}
            size="small"
            placeholder="Note"
            sx={{ minWidth: 200, '& input': { fontSize: '1.3rem' } }}
            slotProps={{ htmlInput: { 'aria-label': 'Note' } }}
          />
        )}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={unenrollReason === ''}
            onClick={() => fireAndForget('[Program] capture unenroll reason', captureReason)}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

// Design-refresh sibling of ProgramSwitch: name + enrollment state line with
// the switch on the right; the unenroll-reason gate is unchanged.
const ProgramSwitchRefresh: FC<{
  program: ProgramWithDisplayName
  onToggle: (enrolled: boolean, programUid: string) => void
  isLoading: boolean
  patientId: number
}> = ({ program, onToggle, isLoading, patientId }) => {
  const { patientIsInProgram } = usePatientDetail()
  const [showReasonPanel, setShowReasonPanel] = useState(false)
  const enrolled = patientIsInProgram(program.uid)

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked && requiresUnenrollReason(program.uid)) {
      setShowReasonPanel(true)
      return
    }
    onToggle(event.target.checked, program.uid)
  }

  return (
    <Box sx={{ px: 2, py: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, color: 'text.primary' }}>
            {program.display_name}
          </Typography>
          <Typography
            sx={theme => ({
              mt: 0.25,
              fontSize: '1.2rem',
              color: enrolled ? theme.palette.tones.success.fg : theme.palette.text.secondary,
            })}
          >
            {enrolled ? 'Enrolled' : 'Not enrolled'}
          </Typography>
        </Box>
        <Switch
          checked={enrolled}
          onChange={handleToggle}
          color="primary"
          disabled={isLoading}
          // Via slotProps, not the deprecated `inputProps`: Switch sets its own input
          // slot props in v7 (that is where `role="switch"` comes from) and silently
          // discards `inputProps`, which would leave the control with no accessible name.
          slotProps={{ input: { 'aria-label': `${program.display_name} enrollment` } }}
        />
      </Box>
      {showReasonPanel && requiresUnenrollReason(program.uid) && (
        <UnenrollReasonPanelRefresh
          patientId={patientId}
          programUid={program.uid}
          onToggle={onToggle}
          onClose={() => setShowReasonPanel(false)}
        />
      )}
    </Box>
  )
}

/**
 * Design-refresh Programs tab: the same programs/enrollment queries and
 * mutations as the legacy tab — baseline programs as cards with an icon tile,
 * current-status pill, the PrimaryCare menu, and a redrawn enrollment stepper;
 * clinical programs as ProgramSwitch rows (reused untouched, including the
 * unenroll-reason capture) inside a single card.
 */
const ProgramTab = ({ patient }: { patient: PatientUser }) => {
  const dispatch = useAppDispatch()
  const { refetch: loadEnrolledPrograms } = usePatientDetail()
  const [isTogglingClinical, setIsTogglingClinical] = useState(false)

  const { result: programs, isLoading } = usePrograms()
  const { result: programEnrollments, isLoading: isLoadingEnrollments } =
    useListProgramEnrollmentByProgram(patient.person.id)

  const mappedPrograms: ProgramWithDisplayName[] = programs
    .map(program => ({ ...program, display_name: readableProgramName(program.uid) }))
    .sort((p1, p2) =>
      p1.display_name && p2.display_name && p1.display_name > p2.display_name ? 1 : -1,
    )
  const clinicalPrograms = mappedPrograms.filter(p => p.is_clinical === true)
  const baselinePrograms = mappedPrograms.filter(p => p.is_clinical === false)

  const refreshAfterEnrollmentChanges = async () => {
    try {
      await Promise.all([
        loadEnrolledPrograms(),
        // Reload the patient because some programs (e.g. RTW) use patient
        // state to display additional info.
        dispatch(patientsSlice.thunks.getPatient(patient.id)),
      ])
    } catch (e) {
      logger.error('[Programs] load enrolled programs', e)
      dispatch(queueNotification({ message: `Unable to load enrolled programs`, variant: 'error' }))
    }
  }

  const addToProgram = async (programUid: string) => {
    setIsTogglingClinical(true)
    try {
      const payload: ProgramsAddPatientCreateBodyParams = {
        patient_id: patient.id,
        program_uid: programUid,
      }
      await programsAddPatient.create({ data: payload })
      await refreshAfterEnrollmentChanges()
      dispatch(
        queueNotification({ variant: 'success', message: `Enrolled in the ${programUid} program` }),
      )
    } catch (e) {
      logger.error('[Programs] enroll patient', e)
      dispatch(
        queueNotification({
          message: `Unable to enroll in the ${programUid} program`,
          variant: 'error',
        }),
      )
    } finally {
      setIsTogglingClinical(false)
    }
  }

  const removeFromProgram = async (programUid: string) => {
    setIsTogglingClinical(true)
    try {
      const payload: ProgramsRemovePatientCreateBodyParams = {
        patient_id: patient.id,
        program_uid: programUid,
      }
      await programsRemovePatient.create({ data: payload })
      await refreshAfterEnrollmentChanges()
      dispatch(
        queueNotification({ variant: 'success', message: `Removed from ${programUid} program` }),
      )
    } catch (e) {
      logger.error('[Programs] remove patient', e)
      dispatch(
        queueNotification({
          variant: 'error',
          message: `Unable to remove from the ${programUid} program`,
        }),
      )
    } finally {
      setIsTogglingClinical(false)
    }
  }

  const onProgramToggle = (enrolled: boolean, programUid: string) => {
    void (enrolled ? addToProgram(programUid) : removeFromProgram(programUid))
  }

  if (isLoading) return null

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
      <SectionHeading title="Baseline programs" caption="Firefly products" />
      {!isLoadingEnrollments &&
        baselinePrograms.map(program => (
          <BaselineProgramCard
            key={program.uid}
            program={program}
            enrollments={programEnrollments.get(program.uid) ?? []}
            patient={patient}
          />
        ))}

      <Box sx={{ mt: 3 }}>
        <SectionHeading title="Clinical programs" caption="Internal care pathways" />
        <SectionCard disablePadding>
          <Box
            sx={theme => ({
              '& > * + *': { borderTop: `1px solid ${theme.palette.divider}` },
            })}
          >
            {clinicalPrograms.map(program => (
              <ProgramSwitchRefresh
                key={program.uid}
                program={program}
                onToggle={onProgramToggle}
                isLoading={isTogglingClinical}
                patientId={patient.id}
              />
            ))}
          </Box>
        </SectionCard>
      </Box>
    </Box>
  )
}

export default ProgramTab
